/**
 * Admin Analytics Hooks
 * For tenant admin dashboard with team analytics
 */
import { useQuery } from "@tanstack/react-query";
import type {
  AnalyticsOverview,
  TeamMembersResponse,
  ContactsAnalyticsQuery,
  ContactsAnalyticsResponse,
  ActivityQuery,
  ActivityResponse,
  ChartsQuery,
  ChartsResponse,
  ContactEventsResponse,
} from "@property-reports/api-contract";

const API_BASE = import.meta.env.VITE_API_URL || "";

// ============================================================================
// API Functions
// ============================================================================

async function fetchAnalyticsOverview(): Promise<AnalyticsOverview> {
  const response = await fetch(`${API_BASE}/api/admin/analytics/overview`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch analytics overview");
  }

  return response.json();
}

async function fetchTeamMemberStats(): Promise<TeamMembersResponse> {
  const response = await fetch(`${API_BASE}/api/admin/analytics/team`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch team member stats");
  }

  return response.json();
}

async function fetchContactsAnalytics(
  query: Partial<ContactsAnalyticsQuery>,
): Promise<ContactsAnalyticsResponse> {
  const params = new URLSearchParams();
  if (query.ownerId) params.set("ownerId", query.ownerId);
  if (query.status && query.status !== "all")
    params.set("status", query.status);
  if (query.frequency && query.frequency !== "all")
    params.set("frequency", query.frequency);
  if (query.search) params.set("search", query.search);
  if (query.sortBy) params.set("sortBy", query.sortBy);
  if (query.sortOrder) params.set("sortOrder", query.sortOrder);
  if (query.page) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));

  const response = await fetch(
    `${API_BASE}/api/admin/analytics/contacts?${params.toString()}`,
    { credentials: "include" },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch contacts analytics");
  }

  return response.json();
}

async function fetchActivityTimeline(
  query: Partial<ActivityQuery>,
): Promise<ActivityResponse> {
  const params = new URLSearchParams();
  if (query.limit) params.set("limit", String(query.limit));
  if (query.offset) params.set("offset", String(query.offset));
  if (query.type && query.type !== "all") params.set("type", query.type);

  const response = await fetch(
    `${API_BASE}/api/admin/analytics/activity?${params.toString()}`,
    { credentials: "include" },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch activity timeline");
  }

  return response.json();
}

async function fetchChartsData(
  query: Partial<ChartsQuery>,
): Promise<ChartsResponse> {
  const params = new URLSearchParams();
  if (query.period) params.set("period", query.period);

  const response = await fetch(
    `${API_BASE}/api/admin/analytics/charts?${params.toString()}`,
    { credentials: "include" },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch charts data");
  }

  return response.json();
}

async function fetchContactEvents(
  contactId: string,
): Promise<ContactEventsResponse> {
  const response = await fetch(
    `${API_BASE}/api/admin/analytics/contacts/${contactId}/events`,
    { credentials: "include" },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch contact events");
  }

  return response.json();
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch analytics overview metrics
 */
export function useAnalyticsOverview() {
  return useQuery({
    queryKey: ["admin", "analytics", "overview"],
    queryFn: fetchAnalyticsOverview,
  });
}

/**
 * Hook to fetch team member statistics
 */
export function useTeamMemberStats() {
  return useQuery({
    queryKey: ["admin", "analytics", "team"],
    queryFn: fetchTeamMemberStats,
  });
}

/**
 * Hook to fetch contacts with analytics data
 */
export function useContactsAnalytics(
  query: Partial<ContactsAnalyticsQuery> = {},
) {
  return useQuery({
    queryKey: ["admin", "analytics", "contacts", query],
    queryFn: () => fetchContactsAnalytics(query),
  });
}

/**
 * Hook to fetch activity timeline
 */
export function useActivityTimeline(query: Partial<ActivityQuery> = {}) {
  return useQuery({
    queryKey: ["admin", "analytics", "activity", query],
    queryFn: () => fetchActivityTimeline(query),
  });
}

/**
 * Hook to fetch charts data
 */
export function useChartsData(query: Partial<ChartsQuery> = {}) {
  return useQuery({
    queryKey: ["admin", "analytics", "charts", query],
    queryFn: () => fetchChartsData(query),
  });
}

/**
 * Hook to fetch email events for a specific contact (lazy-loaded on expand)
 */
export function useContactEvents(contactId: string | null) {
  return useQuery({
    queryKey: ["admin", "analytics", "contactEvents", contactId],
    queryFn: () => fetchContactEvents(contactId!),
    enabled: !!contactId,
  });
}
